/* eslint-disable no-param-reassign */
import { SystemFuncArgs } from '@brochington/ecstatic';
import Point from 'editor/geometry/Point';
import { BoxControlPoint } from 'editor/utils/controlPoints';
import { Components } from '../components';
import MoveBoxCPInteraction from '../components/interactions/MoveBoxCPInteraction';
import Markup from '../components/Markup';

enum Directions {
  NE = 'NE',
  SE = 'SE',
  SW = 'SW',
  NW = 'NW',
  None = 'None',
}

function determineCorner(p1: Point, p2: Point): Directions {
  // bottom left to top right
  if (p1.x < p2.x && p1.y < p2.y) {
    return Directions.NE;
  }

  // top left to bottom right
  if (p1.x < p2.x && p1.y > p2.y) {
    return Directions.SE;
  }

  // top right to bottom left
  if (p1.x > p2.x && p1.y > p2.y) {
    return Directions.SW;
  }

  // bottom right to top left
  if (p1.x > p2.x && p1.y < p2.y) {
    return Directions.NW;
  }

  return Directions.None;
}

function updateBoxControlPointRect(
  markup: Markup,
  interaction: MoveBoxCPInteraction,
  minWidth: number | null
) {
  const m = markup.mkMarkup;

  if (!m) {
    return;
  }

  const [left, bottom, right, top] = m.getRect();

  const { currentX, currentY, deltaX, deltaY, controlPoint } = interaction;
  const cx = currentX;
  const cy = currentY;
  const invertBuffer = 1;

  let newLeft = left;
  let newBottom = bottom;
  let newRight = right;
  let newTop = top;

  switch (controlPoint) {
    case BoxControlPoint.LeftTop:
      newLeft = cx + deltaX;
      newTop = cy - deltaY;
      // x inversion
      if (newLeft > right) {
        newRight = newLeft;
        newLeft -= invertBuffer;
        interaction.controlPoint = BoxControlPoint.RightTop;
      }

      // y inversion
      if (newTop < bottom) {
        newBottom = newTop;
        newTop += invertBuffer;
        interaction.controlPoint = BoxControlPoint.LeftBottom;
      }
      break;
    case BoxControlPoint.CenterTop:
      newTop = cy - deltaY;

      // y inversion
      if (newTop < bottom) {
        newBottom = newTop;
        newTop += invertBuffer;
        interaction.controlPoint = BoxControlPoint.CenterBottom;
      }
      break;
    case BoxControlPoint.RightTop:
      newRight = cx - deltaX;
      newTop = cy - deltaY;

      // x inversion
      if (newRight < left) {
        newLeft = newRight;
        newRight += invertBuffer;
        interaction.controlPoint = BoxControlPoint.LeftTop;
      }

      // y inversion
      if (newTop < bottom) {
        newBottom = newTop;
        newTop += invertBuffer;
        interaction.controlPoint = BoxControlPoint.RightBottom;
      }
      break;
    case BoxControlPoint.RightCenter:
      newRight = cx - deltaX;

      //  x inversion
      if (newRight < left) {
        newLeft = newRight;
        newRight += invertBuffer;
        interaction.controlPoint = BoxControlPoint.LeftCenter;
      }
      break;
    case BoxControlPoint.RightBottom:
      newRight = cx - deltaX;
      newBottom = cy - deltaY;
      // x inversion
      if (newRight < left) {
        newLeft = newRight;
        newRight += invertBuffer;
        interaction.controlPoint = BoxControlPoint.LeftBottom;
      }

      // y inversion
      if (newBottom > top) {
        newTop = newBottom;
        newBottom -= invertBuffer;
        interaction.controlPoint = BoxControlPoint.RightTop;
      }
      break;
    case BoxControlPoint.CenterBottom:
      newBottom = cy - deltaY;

      // y inversion
      if (newBottom > top) {
        newTop = newBottom;
        newBottom -= invertBuffer;
        interaction.controlPoint = BoxControlPoint.CenterTop;
      }
      break;
    case BoxControlPoint.LeftBottom:
      newLeft = cx + deltaX;
      newBottom = cy - deltaY;

      // x inversion
      if (newLeft > right) {
        newRight = newLeft;
        newLeft -= invertBuffer;
        interaction.controlPoint = BoxControlPoint.RightBottom;
      }

      // y inversion
      if (newBottom > top) {
        newTop = newBottom;
        newBottom -= invertBuffer;
        interaction.controlPoint = BoxControlPoint.LeftTop;
      }
      break;
    case BoxControlPoint.LeftCenter:
      newLeft = cx + deltaX;

      // x inversion
      if (newLeft > right) {
        newRight = newLeft;
        newLeft -= invertBuffer;
        interaction.controlPoint = BoxControlPoint.RightCenter;
      }
      break;
    case BoxControlPoint.None:
      break;
    default:
  }

  if (minWidth !== null) {
    if (newRight - newLeft <= minWidth) {
      return;
    }
  }

  m.setRect(newLeft, newBottom, newRight, newTop);
}

function updateSquareMarkup(markup: Markup, interaction: MoveBoxCPInteraction) {
  updateBoxControlPointRect(markup, interaction, null);
}

function updateText(markup: Markup, interaction: MoveBoxCPInteraction) {
  // There is a bug in MarkupKit where rendering a FreeText markup with too small
  // of a width makes the app completely lock up. Right now in order to prevent this
  // the width of the text markup is being clamped.
  const minWidth = 40;
  updateBoxControlPointRect(markup, interaction, minWidth);
}

function updateLineMarkup(markup: Markup, interaction: MoveBoxCPInteraction) {
  const m = markup.mkMarkup;

  if (!m) {
    return;
  }

  updateBoxControlPointRect(markup, interaction, null);

  const { currentX, currentY, deltaX, deltaY, controlPoint } = interaction;
  const cx = currentX;
  const cy = currentY;

  const points = m.getPoints() as Point[];

  const p1 = points[0];
  const p2 = points[1];

  const direction = determineCorner(p1, p2);

  const newP1 = { ...p1 };
  const newP2 = { ...p2 };

  switch (controlPoint) {
    case BoxControlPoint.LeftTop:
      switch (direction) {
        case Directions.NE:
          newP1.x = cx + deltaX;
          newP2.y = cy - deltaY;
          break;
        case Directions.SE:
          newP1.x = cx + deltaX;
          newP1.y = cy - deltaY;
          break;
        case Directions.SW:
          newP2.x = cx + deltaX;
          newP1.y = cy - deltaY;
          break;
        case Directions.NW:
          newP2.x = cx + deltaX;
          newP2.y = cy - deltaY;
          break;
        default:
      }

      // x inversion
      if (newP1.x > newP2.x) {
        interaction.controlPoint = BoxControlPoint.RightTop;
      }

      if (newP2.y < newP1.y) {
        interaction.controlPoint = BoxControlPoint.LeftBottom;
      }
      break;
    case BoxControlPoint.CenterTop:
      switch (direction) {
        case Directions.NE:
          newP2.y = cy - deltaY;
          break;
        case Directions.SE:
          newP1.y = cy - deltaY;
          break;
        case Directions.SW:
          newP1.y = cy - deltaY;
          break;
        case Directions.NW:
          newP2.y = cy - deltaY;
          break;
        default:
      }
      break;
    case BoxControlPoint.RightTop:
      switch (direction) {
        case Directions.NE:
          newP2.x = cx - deltaX;
          newP2.y = cy - deltaY;
          break;
        case Directions.SE:
          newP2.x = cx - deltaX;
          newP1.y = cy - deltaY;
          break;
        case Directions.SW:
          newP1.x = cx - deltaX;
          newP1.y = cy - deltaY;
          break;
        case Directions.NW:
          newP1.x = cx - deltaX;
          newP2.y = cy - deltaY;
          break;
        default:
      }

      break;
    case BoxControlPoint.RightCenter:
      switch (direction) {
        case Directions.NE:
          newP2.x = cx - deltaX;
          break;
        case Directions.SE:
          newP2.x = cx - deltaX;
          break;
        case Directions.SW:
          newP1.x = cx - deltaX;
          break;
        case Directions.NW:
          newP1.x = cx - deltaX;
          break;
        default:
      }
      break;
    case BoxControlPoint.RightBottom:
      switch (direction) {
        case Directions.NE:
          newP2.x = cx - deltaX;
          newP1.y = cy - deltaY;
          break;
        case Directions.SE:
          newP2.x = cx - deltaX;
          newP2.y = cy - deltaY;
          break;
        case Directions.SW:
          newP1.x = cx - deltaX;
          newP2.y = cy - deltaY;
          break;
        case Directions.NW:
          newP1.x = cx - deltaX;
          newP1.y = cy - deltaY;
          break;
        default:
      }
      break;
    case BoxControlPoint.CenterBottom:
      switch (direction) {
        case Directions.NE:
          newP1.y = cy - deltaY;
          break;
        case Directions.SE:
          newP2.y = cy - deltaY;
          break;
        case Directions.SW:
          newP2.y = cy - deltaY;
          break;
        case Directions.NW:
          newP1.y = cy - deltaY;
          break;
        default:
      }
      break;
    case BoxControlPoint.LeftBottom:
      switch (direction) {
        case Directions.NE:
          newP1.x = cx + deltaX;
          newP1.y = cy - deltaY;
          break;
        case Directions.SE:
          newP1.x = cx + deltaX;
          newP2.y = cy - deltaY;
          break;
        case Directions.SW:
          newP2.x = cx + deltaX;
          newP2.y = cy - deltaY;
          break;
        case Directions.NW:
          newP2.x = cx + deltaX;
          newP1.y = cy - deltaY;
          break;
        default:
      }
      break;
    case BoxControlPoint.LeftCenter:
      switch (direction) {
        case Directions.NE:
          newP1.x = cx + deltaX;
          break;
        case Directions.SE:
          newP1.x = cx + deltaX;
          break;
        case Directions.SW:
          newP2.x = cx + deltaX;
          break;
        case Directions.NW:
          newP2.x = cx + deltaX;
          break;
        default:
      }
      break;
    default:
  }

  m.setPoints([newP1, newP2]);
}

export const moveBoxControlPointComponents = [Markup, MoveBoxCPInteraction];

export function moveBoxControlPoint(args: SystemFuncArgs<Components>): void {
  const { components } = args;
  const mc = components.get(Markup);
  const interaction = components.get(MoveBoxCPInteraction);

  const { mkMarkup } = mc;

  if (!mkMarkup) {
    return;
  }

  const markupType = mkMarkup.getType();

  switch (markupType) {
    case 'Square': // Right now just Clouds
      updateSquareMarkup(mc, interaction);
      break;
    // case 'Line': // Both Lines and Arrows
    //   updateLineMarkup(mc, interaction);
    //   break;
    case 'FreeText':
      updateText(mc, interaction);
      break;
    default:
      break;
  }
}
